import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';

import { MainLoaderSkeleton } from '@/components/hocs/suspense/with-suspense';
import { RouteChangeTracker } from '@/components/ui/notifications/route-change-tracker';
import { ROUTES, toRoutePath } from '@/constants/routes';
import { retryChunkLoad } from '@/utils/chunk-error-handler';

import { ProtectedRoute } from '../components/auth/protected-route';
import NavigationShell from '../components/layout/navigation-shell';
import { ErrorBoundary } from '../components/ui/error-boundary';
import NewPasswordPage from '../pages/auth/new-password-page';
import { ResetPassword } from '../pages/auth/reset-password-page/reset-password';
import NotFoundPage from '../pages/not-found-page';
import { DashboardRoutes } from './dashboard-routes';
// Route definitions
import { ShellRoutes } from './shell-routes';
// Layout Shells — DashboardShell is heavy and only mounts under /dashboard,
// so lazy-load it to keep it out of the initial entry chunk.
const DashboardShell = React.lazy(() =>
  retryChunkLoad(() => import('../components/layout/dashboard-shell'))
);

const AuthProviders = React.lazy(() =>
  retryChunkLoad(() => import('../components/auth/auth-providers'))
);

const RouteScrollReset = () => {
  const { hash, pathname } = useLocation();

  React.useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  React.useLayoutEffect(() => {
    if (hash) return;

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [hash, pathname]);

  return null;
};

const RootLayout = () => (
  <HelmetProvider>
    <RouteChangeTracker />
    <RouteScrollReset />
    <Outlet />
  </HelmetProvider>
);

const withAuthProviders = (children: React.ReactNode) => (
  <React.Suspense fallback={<MainLoaderSkeleton />}>
    <AuthProviders>{children}</AuthProviders>
  </React.Suspense>
);

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: toRoutePath(ROUTES.HOME),
        element: <NavigationShell />,
        children: ShellRoutes,
      },
      {
        path: toRoutePath(ROUTES.DASHBOARD_BASE),
        element: withAuthProviders(
          <ProtectedRoute>
            <React.Suspense fallback={<MainLoaderSkeleton />}>
              <DashboardShell />
            </React.Suspense>
          </ProtectedRoute>
        ),
        children: DashboardRoutes,
      },
      {
        path: toRoutePath(ROUTES.RESET_PASSWORD),
        element: (
          <ErrorBoundary>
            <ResetPassword />
          </ErrorBoundary>
        ),
      },
      {
        path: `${toRoutePath(ROUTES.RESET_PASSWORD)}/:token`,
        element: (
          <ErrorBoundary>
            <NewPasswordPage />
          </ErrorBoundary>
        ),
      },
      {
        path: '*',
        element: (
          <ErrorBoundary>
            <NotFoundPage />
          </ErrorBoundary>
        ),
      },
    ],
  },
]);

export const MainAppRoutes = () => {
  return <RouterProvider router={router} />;
};

export default MainAppRoutes;
